import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    FaUserShield,
    FaBuilding,
    FaCamera,
    FaUsers,
    FaUserCircle,
    FaSignOutAlt,
} from "react-icons/fa";

import HeroSection from "../../components/HeroSection";
import Billede1 from "../../Images/Billede1.jpg";
import { useLanguage } from "../../context/LanguageContext";

import "./RoleSelect.css";

const RoleSelect: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    const roles: string[] = useMemo(
        () => JSON.parse(sessionStorage.getItem("userRoles") || "[]"),
        []
    );

    const text = {
        title: language === "da" ? "Vælg rolle" : "Choose role",
        subtitle:
            language === "da"
                ? "Du har adgang til flere roller. Vælg hvordan du vil fortsætte:"
                : "You have access to multiple roles. Choose how you want to continue:",
        logout: language === "da" ? "Log ud" : "Log out",
        backHome: language === "da" ? "Til forsiden" : "Back to home",
        noRoles: language === "da" ? "Ingen roller fundet" : "No roles found",
    };

    const roleMeta: Record<
        string,
        { label: string; description: string; icon: React.ReactNode; path: string }
    > = {
        Admin: {
            label: "Admin",
            description:
                language === "da" ? "Fuld adgang til administration" : "Full admin access",
            icon: <FaUserShield />,
            path: "/Admin",
        },
        Club: {
            label: language === "da" ? "Klub" : "Club",
            description:
                language === "da" ? "Klub- og deltagerstyring" : "Club and participant management",
            icon: <FaBuilding />,
            path: "/Club",
        },
        Media: {
            label: "Media",
            description:
                language === "da" ? "Media- og indholdsadgang" : "Media and content access",
            icon: <FaCamera />,
            path: "/Media",
        },
        Team: {
            label: "Team",
            description:
                language === "da" ? "Team-relaterede funktioner" : "Team-related functions",
            icon: <FaUsers />,
            path: "/Team",
        },
        User: {
            label: language === "da" ? "Bruger" : "User",
            description:
                language === "da" ? "Din profil og dine tilmeldinger" : "Your profile and your signups",
            icon: <FaUserCircle />,
            path: "/UserDashboard",
        },
        Volunteer: {
            label: language === "da" ? "Frivillig" : "Volunteer",
            description:
                language === "da" ? "Se events og tilmeld vagter" : "View events and sign up for shifts",
            icon: <FaUserCircle />,
            path: "/",
        },
    };

    const goToRole = (role: string) => {
        const meta = roleMeta[role];

        if (!meta) {
            notifications.show({
                title: text.noRoles,
                message:
                    language === "da"
                        ? `Rollen "${role}" er ikke sat op endnu.`
                        : `Role "${role}" is not configured yet.`,
            });
            navigate("/UserDashboard");
            return;
        }

        sessionStorage.setItem("activeRole", role);
        navigate(meta.path);
    };

    useEffect(() => {
        if (!roles.length) {
            navigate("/login", { replace: true });
            return;
        }
        if (roles.length === 1) {
            goToRole(roles[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogout = () => {
        sessionStorage.clear();
        window.dispatchEvent(new Event("auth-changed"));
        navigate("/login");
    };

    return (
        <div className="role-page">
            <HeroSection
                titleEn="Become a volunteer for Badminton Denmark Events"
                titleDa="Bliv frivillig til Badminton Danmarks Events"
                descriptionEn="Welcome to the volunteer portal, where you can sign up as a volunteer for Badminton Denmark events!"
                descriptionDa="Velkommen til frivilligportalen, hvor du kan tilmelde dig som frivillig til Badminton Danmarks arrangementer!"
                imageSrc={Billede1}
            />

            <div className="role-card-wrap">
                <Card shadow="lg" radius="md" padding="lg" className="role-card">
                    <Stack gap="xs">
                        <Title order={2}>{text.title}</Title>
                        <Text c="dimmed">{text.subtitle}</Text>
                    </Stack>

                    <Stack mt="lg" gap="sm">
                        {roles.map((role) => {
                            const meta = roleMeta[role] || {
                                label: role,
                                description: language === "da" ? "Åbn rolle" : "Open role",
                                icon: <FaUserCircle />,
                                path: "/UserDashboard",
                            };

                            return (
                                <Button
                                    key={role}
                                    variant="default"
                                    size="lg"
                                    radius="md"
                                    onClick={() => goToRole(role)}
                                    className="role-btn"
                                    styles={{ inner: { justifyContent: "space-between" } }}
                                >
                                    <Group gap="sm" wrap="nowrap">
                                        <span className="role-icon">{meta.icon}</span>

                                        <div style={{ textAlign: "left" }}>
                                            <Text fw={800}>{meta.label}</Text>
                                            <Text size="xs" c="dimmed">
                                                {meta.description}
                                            </Text>
                                        </div>
                                    </Group>

                                    <Text size="sm" c="dimmed">
                                        →
                                    </Text>
                                </Button>
                            );
                        })}
                    </Stack>

                    <Group justify="space-between" mt="xl">
                        <Button variant="subtle" color="red" onClick={() => navigate("/")}>
                            {text.backHome}
                        </Button>

                        <Button
                            color="red"
                            variant="light"
                            leftSection={<FaSignOutAlt />}
                            onClick={handleLogout}
                        >
                            {text.logout}
                        </Button>
                    </Group>
                </Card>
            </div>
        </div>
    );
};

export default RoleSelect;
